import { useTitle } from '@arema/components/Hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Groupper, Header, Modal, Table } from 'react-frontier2';
import { useTranslation } from 'react-i18next';
import { ShortUser, SystemAccess, SystemAccessType, SystemAccessTypes } from '@arema/components/Classes';
import { Link } from 'react-router-dom';
import { useUser } from '../../AdminHooks';
import { bindClick } from '@arema/components/Util';
import API from '../../API';
import UserAccess from '../../UserAccess';

enum Modals{
	FILTER = 1,
}

const SystemFilters : {
	value: SystemAccessTypes,
	text: string,
}[] = [
	{ value: 'ADMIN', text: 'Admins' },
	{ value: 'REPORTS', text: 'Empresarios' },
	{ value: 'OPERATIONS', text: 'Operaciones' },
	{ value: 'SCANNER', text: 'Scanners' },
	{ value: 'PDV', text: 'Taquilleros' },
]

const USER_COUNT = 30;
const SAVE_FILTERS_KEY = 'user_list_filters'
var UserList = ()=>{
	var { t } = useTranslation();
	var { hasAccess } = useUser();
	var { setTitle } = useTitle();
	var [query, setQuery] = useState<string>('');
	var [users, setUsers] = useState<ShortUser[]>(null);
	var [searchResults, setSearchResults] = useState<ShortUser[]>(null);
	var [page, setPage] = useState<number>(0);
	var [searching, setSearching] = useState<boolean>(false);
	var [loadError, setLoadError] = useState<string>(null);
	var [searchTimeout, setSearchTimeout] = useState(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [systemFilters, setSystemFilters] = useState<SystemAccessType>({
		// ADMIN: 
	});
	var [systemFiltersSettings, setSystemFiltersSettings] = useState<SystemAccessType>({
		// ADMIN: true
	});

	useEffect(()=>{
		setTitle('Usuarios');

		var local_filters : SystemAccessType = { };
		var s_local_filters = localStorage.getItem(SAVE_FILTERS_KEY);
		if(!!s_local_filters){
			try{
				local_filters = JSON.parse(s_local_filters);
				setSystemFilters(local_filters);
			}catch(e){
				localStorage.removeItem(SAVE_FILTERS_KEY);
			}
		}

		API.getUserList(null, 0, USER_COUNT, local_filters).then(res=>{
			if(res.error) return setLoadError(res.message);
			setUsers(res.data);
		}).catch(err=>{
			setLoadError('Hubo un error inesperado cargando los usuarios (LCL-1)');
		});
	}, []);

	var searchUsers = (allow_empty: boolean=false, filters?: SystemAccessType)=>{
		if(!allow_empty && query.length<3) return;
		setSearching(true);
		API.getUserList(query, page*USER_COUNT, USER_COUNT, (filters || systemFilters)).then(res=>{
			if(res.error) return setLoadError(res.message);
			setSearchResults(res.data);
		}).catch(err=>{
			setLoadError('Hubo un error buscando los usuarios (LCL-1)');
		})
	}

	var onChangeSearch = (val: string)=>{
		if(searchTimeout) clearTimeout(searchTimeout);
		setQuery(val);
		setSearchResults(null);
		if(val.length<3){
			setSearching(false);
			setSearchResults(null);
			return;
		}
		setSearching(true);
		setSearchTimeout(setTimeout(()=>{
			searchUsers();
		}, 1000))
	}
	
	var showFilters = ()=>{
		setSystemFiltersSettings(systemFilters);
		setShownModal(Modals.FILTER);
	}
	
	var changeFilter = (system: SystemAccessTypes, val: boolean)=>{
		return ()=>{
			var flt = {...systemFiltersSettings};
			flt[system] = val;
			console.log(system, val);
			setSystemFiltersSettings(flt);
		}
	}

	var applyFilters = ()=>{
		setSystemFilters(systemFiltersSettings);
		setShownModal(null);
		searchUsers(true, systemFiltersSettings);
		localStorage.setItem(SAVE_FILTERS_KEY, JSON.stringify(systemFiltersSettings));
	}

	var filter_text = useMemo(()=>{
		var text = [];
		for(var i in systemFilters){
			if(systemFilters[i as SystemAccessTypes]){
				var fl = SystemFilters.find(a=>a.value==(i as SystemAccessTypes));
				if(fl) text.push(fl.text);
			}
		}
		if(text.length==0 || text.length>=SystemFilters.length){
			return 'Todos los usuarios';
		}
		return text.join(', ');
	}, [systemFilters]);

	return <div className="ar user-list-screen">
		<Groupper style={{ maxWidth: 800 }} title={'Usuarios'} className='catalog' titleRight={(
			hasAccess(UserAccess.USERS.CREATE_USERS) ? (
				<Button text='Nuevo' color='black' iconName='plus-circle' as={Link} to={'/users/create'} />
			) : null
		)}>
			<Groupper.DividerInput value={query} removeBorder={['top']} removeMargin={['top']} onChange={onChangeSearch} style={{ marginBottom: 0 }} placeholder='Buscar usuarios' onSubmit={()=>searchUsers()} buttons={(
				<Button text='Buscar' onClick={()=>searchUsers()} />
			)} />
			<div className="filters" onClick={showFilters}>
				<div className="text">
					<b>Mostrando</b>: {filter_text}
				</div>
			</div>
			<Table last fitted>
				<Table.Head
					collapsingIndexes={[0]}
					data={[['ID', 'Nombre', 'Apellido', 'Correo', 'Tipo']]}
				/>
				<Table.Body>
					{loadError ? (
						<Table.Cell row colSpan={100}>
							<Header style={{ marginTop: 20, marginBottom: 20 }} text={'Error'} subtext={loadError} />
						</Table.Cell>
					) : <>
						{searching && !!searchResults && searchResults.length>0 && query.length>0 && (
							<Table.Cell row colSpan={1000} style={{ textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>Resultados de <b>"{query}"</b></Table.Cell>
						)}
						{searching && !!searchResults && searchResults.length==0 && (
							<Table.Cell row colSpan={100}>
								<Header style={{ marginTop: 20, marginBottom: 20 }} text={'Sin resultados'} subtext={`No se encontraron resultados con "${query}"`} />
							</Table.Cell>
						)}
						{(searching ? searchResults : users)===null ? (
							<Table.Cell row colSpan={100}>
								<Header loading text={searching ? `Buscando "${query}"` : 'Cargando usuarios'} />
							</Table.Cell>
						) : (
							(searchResults || users).map(a=>(
								<Table.Row selectable compact
									key={`USR-${a.user_id}`}
									collapsingIndexes={[0, 1, 2, 4]}
									data={[a.user_id, a.first_name, a.last_name, a.email, a.user_type]}
									as={Link}
									to={`/users/${a.user_id}`}
								/>
							))
						)}
					</>}
				</Table.Body>
			</Table>
		</Groupper>
		<Modal open={shownModal===Modals.FILTER} size='mini' onClose={bindClick(setShownModal)}>
			<Modal.Header>Filtrar usuarios</Modal.Header>
			<Modal.Content>
				<Table fitted top last>
					<Table.Body>
						{SystemFilters.map(a=>(
							<Table.Row selectable onClick={changeFilter(a.value, !systemFiltersSettings[a.value])}>
								<Table.Cell collapsing style={{ paddingRight: 10, paddingLeft: 10 }} value={<Checkbox checked={systemFiltersSettings[a.value]} onChange={v=>changeFilter(a.value, v)} />} />
								<Table.Cell value={a.text} />
							</Table.Row>
						))}
					</Table.Body>
				</Table>
				{/* <div className="fr selectable divided items">
					{SystemFilters.map(a=>(
						<div className="item" style={{ padding: 15 }} key={`FLT-${a.value}`} onClick={changeFilter(a.value, !systemFiltersSettings[a.value])}>
							<Checkbox checked={systemFiltersSettings[a.value]} />
							<div className="text" style={{ marginLeft: 5 }}>{a.text}</div>
						</div>
					))}
				</div> */}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
				<Button text='Filtrar' color='black' onClick={applyFilters} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default UserList;